import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaSpinner, FaCheck, FaExpandAlt, FaLightbulb, FaEdit, FaTrash, FaSync, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import './IdeaNode.css';
import RewriteModal from './RewriteModal';
import { supabase } from '../lib/supabase'; // Import supabase instance

interface Idea {
  id: string;
  content: string;
  children: Idea[];
  parent_id: string | null;
  position: number;
}

interface IdeaNodeProps {
  idea: Idea;
  index: string | number;
  level?: number;
  onExpand: (id: string) => void;
  onAddSimilar: (id: string, content: string) => void;
  onSelect: (id: string) => void;
  onCollapseChildren: (id: string) => void;
  onDelete: (id: string) => void;
  onRegenerate: (id: string) => void;
  expandedIdeas: Record<string, string | null>;
  expandingIdea: string | null;
  selectedIdeas: Set<string>;
  generatingSimilar: string | null;
  onRewrite: (id: string, content: string, prompt: string, regenerate: boolean) => void;
  rewritingIdea: string | null;
  deletingIdea: string | null;
  regeneratingIdea: string | null;
}

const IdeaNode: React.FC<IdeaNodeProps> = ({ 
  idea, 
  index, 
  level = 0,
  onExpand, 
  onAddSimilar, 
  onSelect,
  onCollapseChildren,
  onDelete,
  onRegenerate,
  expandedIdeas,
  expandingIdea,
  selectedIdeas,
  generatingSimilar,
  onRewrite,
  rewritingIdea,
  deletingIdea,
  regeneratingIdea,
}) => {
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const hasChildren = idea.children && idea.children.length > 0;

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  const handleSave = async (content: string) => {
    try {
      // Update in database
      const { error } = await supabase
        .from('ideas')
        .update({ content })
        .eq('id', idea.id);

      if (error) throw error;

      // Update local state through parent
      onRewrite(idea.id, content, '', false);
    } catch (error) {
      console.error('Error saving idea:', error);
    } finally {
      setShowRewriteModal(false);
    }
  };

  return (
    <div className={`idea-node level-${level}`}>
      <div className="idea-toolbar">
        <button
          className={`icon-button select-button ${selectedIdeas.has(idea.id) ? 'selected' : ''}`}
          onClick={() => onSelect(idea.id)}
          data-tooltip-id="main-tooltip"
          data-tooltip-content={selectedIdeas.has(idea.id) ? "Unselect idea" : "Select idea"}
        >
          <FaCheck />
        </button>
        <button
          className="icon-button"
          onClick={() => onAddSimilar(idea.id, idea.content)}
          disabled={generatingSimilar === idea.id}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Add similar ideas"
        >
          {generatingSimilar === idea.id ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaLightbulb />
          )}
        </button>
        <button
          className="icon-button"
          onClick={() => onExpand(idea.id)}
          disabled={expandingIdea === idea.id}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Expand this idea"
        >
          {expandingIdea === idea.id ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaExpandAlt />
          )}
        </button>
        <button
          className="icon-button"
          onClick={() => setShowRewriteModal(true)}
          disabled={rewritingIdea === idea.id}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Rewrite idea"
        >
          {rewritingIdea === idea.id ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaEdit />
          )}
        </button>
        <button
          className="icon-button"
          onClick={() => onRegenerate(idea.id)}
          disabled={regeneratingIdea === idea.id}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Regenerate idea"
        >
          {regeneratingIdea === idea.id ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaSync />
          )}
        </button>
        <button
          className="icon-button"
          onClick={() => onDelete(idea.id)}
          disabled={deletingIdea === idea.id}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Delete idea"
        >
          {deletingIdea === idea.id ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaTrash />
          )}
        </button>
      </div>
      <div className="idea-content">
        <div className="idea-header">
          <span className="idea-number">{index}</span>
          {hasChildren && (
            <button className="collapse-button" onClick={toggleCollapse}>
              {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            </button>
          )}
          <div className="idea-text">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{idea.content}</ReactMarkdown>
          </div>
        </div>

        <RewriteModal
          isOpen={showRewriteModal}
          onClose={() => setShowRewriteModal(false)}
          onSave={handleSave}
          currentContent={idea.content}
        />

        {!isCollapsed && hasChildren && (
          <div className="children">
            {idea.children.map((child, childIndex) => (
              <IdeaNode
                key={child.id}
                idea={child}
                index={`${index}.${childIndex + 1}`}
                level={level + 1}
                onExpand={onExpand}
                onAddSimilar={onAddSimilar}
                onSelect={onSelect}
                onCollapseChildren={onCollapseChildren}
                onDelete={onDelete}
                onRegenerate={onRegenerate}
                expandedIdeas={expandedIdeas}
                expandingIdea={expandingIdea}
                selectedIdeas={selectedIdeas}
                generatingSimilar={generatingSimilar}
                onRewrite={onRewrite}
                rewritingIdea={rewritingIdea}
                deletingIdea={deletingIdea}
                regeneratingIdea={regeneratingIdea}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeaNode;